import { HTTP_INTERCEPTORS } from '@angular/common/http'
import { EnvironmentProviders, inject, Provider, provideAppInitializer } from '@angular/core'

import {
  MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService,
  MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular'
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser'
import { environment } from 'environments/environment'

import { MsalInitService } from '@app/auth/msal-init.service'
import { bridgeRedirectUri } from '@app/auth/msal-redirect-bridge'
import { MsalTelemetryService } from '@app/auth/msal-telemetry'

const GRAPH_ME_ENDPOINT = 'https://graph.microsoft.com/v1.0/me'
// avatars call /users/{email}/photos/48x48/$value, so this needs a wildcard, not a bare base url
const GRAPH_USERS_ENDPOINT = 'https://graph.microsoft.com/v1.0/users/*'

export function loggerCallback (logLevel: LogLevel, message: string) {
  console.log(message)
}

export function MSALInstanceFactory (): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.ssoClientId,
      authority: `https://login.microsoftonline.com/${environment.ssoTenantId}`,
      // the redirect bridge, which main.ts runs instead of bootstrapping the app (acted-core#252)
      redirectUri: bridgeRedirectUri(document.baseURI),
      // deliberately left at the root: a logout carries no authorization response, so sending it
      // through the bridge would only reach the branch where the bridge throws
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  })
}

/**
 * Remove trailing slashes with a single backward scan.
 *
 * A `/\/+$/` replace would do the same, but the unanchored `+` makes the engine retry from every
 * position, which SonarQube flags as non-linear backtracking (S8786).
 */
function stripTrailingSlashes (value: string): string {
  let end = value.length
  while (end > 0 && value.charAt(end - 1) === '/') end -= 1
  return value.slice(0, end)
}

/**
 * Build the MsalInterceptor protected resource map from the comma-separated list of API origins
 * injected at runtime (`SSO_ALLOWED_ORIGINS`).
 *
 * msal-angular v5 matches these keys strictly: a bare base url only ever matches itself, so it
 * has to carry an explicit `/*` wildcard to cover the sub-paths we actually call. The Graph "me"
 * entry is an exact path and needs no wildcard.
 */
export function buildProtectedResourceMap (origins: string | undefined, scope: string): Map<string, string[]> {
  const protectedResourceMap = new Map<string, string[]>()
  protectedResourceMap.set(GRAPH_ME_ENDPOINT, ['user.read'])
  protectedResourceMap.set(GRAPH_USERS_ENDPOINT, ['ProfilePhoto.Read.All'])

  origins?.split(',').forEach((origin: string) => {
    const base = stripTrailingSlashes(origin.trim())
    if (base) protectedResourceMap.set(`${base}/*`, [scope])
  })

  return protectedResourceMap
}

export function MSALInterceptorConfigFactory (): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: buildProtectedResourceMap(environment.ssoAllowedOrigins, `${environment.ssoClientId}/core-user`),
    // keys above are wildcard/exact paths, so keep the safer v5 matching (also silences the runtime warning)
    strictMatching: true,
  }
}

export function MSALGuardConfigFactory (): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/',
  }
}

/**
 * Everything MSAL needs in a standalone bootstrap, replacing the `MsalModule` import.
 *
 * Keep this spread ahead of any other HTTP_INTERCEPTORS provider: DI multi-provider order is
 * declaration order, and MsalInterceptor has to run before the other interceptors.
 */
export function provideMsal (): Array<Provider | EnvironmentProviders> {
  return [
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    // msal-angular v5 does not auto-initialize on loginRedirect/logoutRedirect (that lands in v6),
    // so initialize once at bootstrap instead of ad hoc in components and services.
    provideAppInitializer(() => inject(MsalInitService).ready),
    // instantiating the service is what subscribes it to the event stream; there is nothing to await
    provideAppInitializer(() => { inject(MsalTelemetryService) }),
  ]
}
